import { useEffect, useRef } from 'react';
import { useStore } from '~/stores';
import { deviceTokenService } from '~/services/auth/DeviceToken';
import remoteOpsService from '~/services/remote-ops/RemoteOpsService';
import { isDemoMode } from '~/services/demo/DemoModeService';
import { resolveApiBaseUrl } from '~/services/embedded/hostConfig';

/**
 * Inicializa o {@code RemoteOpsService} — cliente SSE+REST da
 * Visualização Interativa.
 *
 * <p>Abre {@code EventSource} em
 * {@code /api/sse/remote/device/{playerId}?token=...} pra receber comandos
 * do admin e responde via {@code POST /api/v1/remote-player/...}. Migrou
 * de WebSocket pra SSE+REST pra consistência com o resto do Efflux.</p>
 *
 * <p>Mesmas premissas do {@link useSyncInit}: espera o
 * {@code authStore.playerId} (UUID real, não temporário ou external_id)
 * ficar disponível, cleanup no unmount, no-op em demo mode.</p>
 */
export const useRemoteOpsInit = () => {
  const { authStore } = useStore();
  const initialized = useRef(false);

  useEffect(() => {
    if (isDemoMode()) return;
    if (initialized.current) return;

    if (!authStore.deviceId || authStore.deviceId.startsWith('efflux-temp-')) {
      // Aguarda hardware ID real — mesma estratégia do useSyncInit.
      return;
    }

    // Path do SSE/REST aceita UUID (tab_players.id) OU external_id (CHROME-XXX).
    // Preferimos playerId (UUID) quando disponível; cai pra deviceId
    // (external_id) pra players legados sem UUID propagado pro authStore.
    // Backend resolve internamente via DeviceTokenAuthService.
    const playerRef = authStore.playerId ?? authStore.deviceId;

    const apiUrl = resolveApiBaseUrl();

    const deviceToken = deviceTokenService.getToken();

    console.log('[useRemoteOpsInit] iniciando RemoteOpsService', {
      apiUrl,
      playerRef,
      source: authStore.playerId
        ? 'authStore.playerId (UUID)'
        : 'authStore.deviceId (external_id)',
      tokenPrefix: deviceToken.slice(0, 8),
    });

    remoteOpsService.configure({
      apiUrl,
      deviceId: playerRef,
      deviceToken,
    });
    remoteOpsService.start();
    initialized.current = true;

    return () => {
      remoteOpsService.stop();
      initialized.current = false;
    };
  }, [authStore.deviceId, authStore.playerId]);
};

export default useRemoteOpsInit;
